import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { machineryService } from '../../../services/machineryService';
import Toast from '../../../components/Common/Toast';

const EditMachine = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    brand: '',
    model: '',
    serial_number: '',
    type: 'excavadora',
    category_id: '',
    status: 'active',
    propio: 'propio',
    control_hour_meter: false,
    control_mileage: false,
    maintenance_alert_hours_interval: '',
    last_maintenance_hours: '',
    maintenance_alert_km_interval: '',
    last_maintenance_km: '',
    notes: ''
  });

  useEffect(() => {
    loadMachine();
  }, [id]);

  useEffect(() => {
    const loadCategories = async () => {
      const res = await machineryService.getCategories({ scope: 'machine' });
      if (res.success && Array.isArray(res.data)) {
        setCategories(res.data);
      }
    };
    loadCategories();
  }, []);

  const loadMachine = async () => {
    setLoading(true);
    try {
      const res = await machineryService.getMachineryFormData({ machine_id: id });
      if (res.success && res.data?.machine) {
        const machine = res.data.machine;
        setFormData({
          code: machine.code || '',
          name: machine.name || '',
          brand: machine.brand || '',
          model: machine.model || '',
          serial_number: machine.serial_number || '',
          type: machine.type || 'excavadora',
          category_id: machine.category_id != null ? String(machine.category_id) : '',
          status: machine.status || 'active',
          propio: machine.propio || 'propio',
          control_hour_meter: !!machine.control_hour_meter,
          control_mileage: !!machine.control_mileage,
          maintenance_alert_hours_interval: machine.maintenance_alert_hours_interval != null ? String(machine.maintenance_alert_hours_interval) : '',
          last_maintenance_hours: machine.last_maintenance_hours != null ? String(machine.last_maintenance_hours) : '',
          maintenance_alert_km_interval: machine.maintenance_alert_km_interval != null ? String(machine.maintenance_alert_km_interval) : '',
          last_maintenance_km: machine.last_maintenance_km != null ? String(machine.last_maintenance_km) : '',
          notes: machine.notes || ''
        });
      } else {
        setToast({
          isOpen: true,
          message: res.message || 'Máquina no encontrada',
          type: 'error'
        });
        setTimeout(() => navigate('/machinery/machines'), 2000);
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: 'Error al cargar la máquina',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        category_id: formData.category_id ? parseInt(formData.category_id, 10) : null,
        maintenance_alert_hours_interval: formData.maintenance_alert_hours_interval ? parseFloat(formData.maintenance_alert_hours_interval) : null,
        last_maintenance_hours: formData.last_maintenance_hours ? parseFloat(formData.last_maintenance_hours) : null,
        maintenance_alert_km_interval: formData.maintenance_alert_km_interval ? parseInt(formData.maintenance_alert_km_interval, 10) : null,
        last_maintenance_km: formData.last_maintenance_km ? parseInt(formData.last_maintenance_km, 10) : null
      };
      const response = await machineryService.updateMachine(id, data);
      if (response.success) {
        setToast({
          isOpen: true,
          message: 'Máquina actualizada exitosamente',
          type: 'success'
        });
        setTimeout(() => {
          navigate('/machinery/machines');
        }, 1500);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al actualizar la máquina',
          type: 'error'
        });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: 'Error al actualizar la máquina',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <p className="mt-2 text-gray-600">Cargando máquina...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Editar Máquina</h1>
          <p className="text-gray-600 mt-1">Modifique la información de la máquina</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Código *
              </label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Marca *
              </label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Modelo *
              </label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número de Serie
              </label>
              <input
                type="text"
                name="serial_number"
                value={formData.serial_number}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="excavadora">Excavadora</option>
                <option value="cargador">Cargador</option>
                <option value="compactador">Compactador</option>
                <option value="retroexcavadora">Retroexcavadora</option>
                <option value="rodillo">Rodillo</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoría (opcional)
              </label>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Sin categoría</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estado *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="active">Activa</option>
                <option value="maintenance">En Mantenimiento</option>
                <option value="inactive">Inactiva</option>
                <option value="retired">Retirada</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ¿Es propio? *
              </label>
              <select
                name="propio"
                value={formData.propio}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="propio">Propio</option>
                <option value="alquilado">Alquilado</option>
              </select>
            </div>

            <div className="md:col-span-2 border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Control de uso
              </label>
              <p className="text-sm text-gray-500 mb-3">Indique si se llevará control de horómetro y/o kilometraje para esta máquina (puede marcar ambos)</p>
              <div className="flex flex-wrap gap-6">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="control_hour_meter"
                    checked={!!formData.control_hour_meter}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700">Controlar horómetro</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="control_mileage"
                    checked={!!formData.control_mileage}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700">Controlar kilometraje</span>
                </label>
              </div>
            </div>

            {formData.control_hour_meter && (
            <div className="md:col-span-2 border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Alerta de mantenimiento (por horómetro)</label>
              <p className="text-sm text-gray-500 mb-3">Si define un intervalo (ej. 500 horas), el sistema mostrará una alerta en Mantenimientos cuando la máquina se acerque o supere esas horas.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Intervalo de alerta (horas)</label>
                  <input type="number" name="maintenance_alert_hours_interval" value={formData.maintenance_alert_hours_interval} onChange={handleChange} step="0.1" min="0" placeholder="Ej. 500" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Último mantenimiento en (horas)</label>
                  <input type="number" name="last_maintenance_hours" value={formData.last_maintenance_hours} onChange={handleChange} step="0.1" min="0" placeholder="Opcional" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
              </div>
            </div>
            )}

            {formData.control_mileage && (
            <div className="md:col-span-2 border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Alerta de mantenimiento (por kilometraje)</label>
              <p className="text-sm text-gray-500 mb-3">Si define un intervalo (ej. 50 000 km), el sistema mostrará una alerta en Mantenimientos cuando la máquina se acerque o supere ese kilometraje.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Intervalo de alerta (km)</label>
                  <input type="number" name="maintenance_alert_km_interval" value={formData.maintenance_alert_km_interval} onChange={handleChange} min="1" placeholder="Ej. 50000" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Último mantenimiento en (km)</label>
                  <input type="number" name="last_maintenance_km" value={formData.last_maintenance_km} onChange={handleChange} min="0" placeholder="Opcional" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
              </div>
            </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notas
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          <div className="mt-6 flex gap-4">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/machinery/machines')}
              className="px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default EditMachine;
